import type React from "react";
import {Skeleton, Typography} from "@mui/material";
import {StatsCard, type StatsCardItem} from "~/components/Card/StatsCard";
import {EMPTY_CELL} from "~/components/data-grid/cells/gridCellFormat";
import StoreIcon from '~/icons/svg/StoreIcon.svg?react'
import VerifiedIcon from '~/icons/svg/VerifiedIcon.svg?react'
import ScheduleIcon from '~/icons/svg/ScheduleIcon.svg?react'
import WarningCircleIcon from '~/icons/svg/WarningCircleIcon.svg?react'
import InfoIcon from '~/icons/svg/InfoIcon.svg?react'
import {useStores} from "~/stores";
import type {BuQualificationKpiSummary} from "~/pages/supplier-360/types/Supplier360Types";
import {useBuQualificationKpiSummaryQuery} from "~/pages/supplier-360/tabs/SupplierCompliance/hooks/useBuQualificationKpiSummaryQuery";
import {useComplianceBuAggregatesQuery} from "~/pages/supplier-360/tabs/SupplierCompliance/hooks/useComplianceBuGroupsQuery";

/**
 * The Business units view's 5 KPI cells (`SxcBuKpis`): BU counts by `status` from `kpi_summary`
 * (§2.7). "Requirement relationships" is the BU groups' `total_agg` summed (§2.8a) — a company-wide
 * record linked to several BUs counts once per BU.
 */
function buildKpiItems(summary: BuQualificationKpiSummary | undefined, relationships: number | undefined): StatsCardItem[] {
    const value = (n: number | undefined) => n === undefined ? EMPTY_CELL : String(n);

    return [
        {
            icon: <StoreIcon/>,
            iconColor: 'primary',
            label: 'Business units',
            value: value(summary?.count),
            tagText: relationships === undefined ? EMPTY_CELL : `${relationships} requirement relationships`,
        },
        {
            icon: <VerifiedIcon/>,
            iconColor: 'success',
            label: 'Compliant',
            value: value(summary?.compliant),
            tagText: 'every applicable record met',
        },
        {
            icon: <ScheduleIcon/>,
            iconColor: 'warning',
            label: 'Expiring soon',
            value: value(summary?.expiring_soon),
            tagText: 'action within 60 days',
        },
        {
            icon: <WarningCircleIcon/>,
            iconColor: 'error',
            label: 'Non-compliant',
            value: value(summary?.non_compliant),
            tagText: 'missing or lapsed evidence',
        },
        {
            icon: <InfoIcon/>,
            iconColor: 'info',
            label: 'Pending review',
            value: value(summary?.pending_review),
            tagText: 'awaiting verification',
        },
    ];
}

type ComplianceBuKpisProps = {
    entityId: number;
};

/** The Business units view's KPI strip — fetches its own `kpi_summary` and BU groups. */
export const ComplianceBuKpis: React.FC<ComplianceBuKpisProps> = ({entityId}) => {
    const {bagStore} = useStores();
    const {data: summary, isLoading, isError} = useBuQualificationKpiSummaryQuery(entityId);
    const {data: groups} = useComplianceBuAggregatesQuery(entityId, bagStore.bagId ?? undefined);

    const relationships = groups
        ?.filter(group => group.id !== null)
        .reduce((total, group) => total + group.total_agg, 0);

    if (isLoading) {
        // The skeleton takes its size from the empty strip inside it, so nothing jumps when data lands.
        return (
            <Skeleton variant="rectangular" sx={{maxWidth: 'none', borderRadius: 1}}>
                <StatsCard items={buildKpiItems(undefined, undefined)}/>
            </Skeleton>
        );
    }

    if (isError) {
        return (
            <Typography color="text.secondary">
                Could not load business unit KPIs.
            </Typography>
        );
    }

    return <StatsCard items={buildKpiItems(summary, relationships)}/>;
};
